import { useState } from "react";
import { FaQuoteLeft, FaQuoteRight } from "react-icons/fa";
import { API, graphqlOperation } from "aws-amplify";
import { searchProducts as searchProductsQuery } from "../../graphql/queries";
import { buildProductSearchVars } from "../../utils/buildSearchVars";
import AsyncSelect from "react-select/async";
import Select, { components, DropdownIndicatorProps } from "react-select";
import { extractSearchProductsV2 } from "../../helpers/extract";
import {
  brandOptions,
  MediaUri,
  productSearchStatusOptions,
} from "../../constants/enum";
import { BiSearchAlt } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import BadgeProductStatus from "../../components/BadgeProductStatus";
import ButtonPillBrand from "../../components/ButtonPillBrand";
import { FcFilledFilter } from "react-icons/fc";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { format, formatDistanceToNow, differenceInDays } from "date-fns";

interface RequiredProps {}

interface OptionalProps {
  searchInput: string;
  brand: string;
  status: string;
  enableUpdatedDateSearch: boolean;
  startSearchUpdatedDate: Date;
  endSearchUpdatedDate: Date;
}

const defaultProps: OptionalProps = {
  searchInput: "",
  brand: "ALL",
  status: "ALL",
  enableUpdatedDateSearch: false,
  startSearchUpdatedDate: new Date(new Date().setDate(-30)),
  endSearchUpdatedDate: new Date(),
};

interface Props extends RequiredProps, OptionalProps {}

SearchBar.defaultProps = defaultProps;

export type SearchOption = {
  readonly value: string;
  readonly label: string;
  readonly color: string;
  readonly isFixed?: boolean;
  readonly isDisabled?: boolean;
};

const searchOptions = [{ value: "ALL", label: "All Brand" }, ...brandOptions];

const CustomOption = ({ innerRef, innerProps, value, label }: any) => {
  if (value === "custom") {
    return (
      <div ref={innerRef} {...innerProps}>
        <div className="flex flex-row justify-center gap-2 p-2">
          <FaQuoteLeft />
          <span className="text-lg font-bold">{label}</span>
          <FaQuoteRight />
        </div>
      </div>
    );
  }

  const noImage = "/brands/no_image.png";
  const images =
    value !== "custom" && value.images ? JSON.parse(value.images) : [];
  const imageName = images.length > 0 ? images[0] : noImage;
  const imagePath = `${MediaUri}/thumbnail/${imageName}`;
  const updatedDateText = formatDistanceToNow(new Date(value.updatedAt));
  const diff = differenceInDays(new Date(value.updatedAt), new Date());

  return (
    <div ref={innerRef} {...innerProps}>
      <div
        className={`hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100 selection:bg-slate-100 ${
          innerRef && "bg-slate-100"
        }`}
      >
        <a
          href={`/product/${value.id}/show`}
          target="blank"
          className={`flex flex-row gap-2 p-2 
        }`}
        >
          <img
            className="object-contain w-11"
            src={imagePath}
            alt={imageName}
            onError={(e: any) => {
              e.target.src = noImage;
            }}
          />
          <div className="grid grid-rows-3">
            <span className="font-bold">
              {value.name} [ Bar : {value.barcode} ]
            </span>
            <span className="text-xs">{value.titleTH}</span>
            <span className="text-xs">{value.titleEN}</span>
          </div>
        </a>
        <div className="m-2">
          {diff ? (
            <span className=" inline-block whitespace-nowrap rounded-[0.27rem] bg-primary-100 px-[0.65em] pb-[0.25em] pt-[0.35em] text-center align-baseline text-[0.75em] font-bold leading-none text-primary-700">
              {updatedDateText} ago
            </span>
          ) : (
            <span className="animate-bounce inline-block whitespace-nowrap rounded-[0.27rem] bg-danger-100 px-[0.65em] pb-[0.25em] pt-[0.35em] text-center align-baseline text-[0.75em] font-bold leading-none text-danger-700">
              New
            </span>
          )}
        </div>
        <div className={`pl-2 pb-2 flex flex-wrap gap-2`}>
          <ButtonPillBrand brand={value.brand} textSize="text-xs" />
          <BadgeProductStatus status={value.status} textSize="text-xs" />
        </div>
      </div>
    </div>
  );
};

const dateFormat = "yyyy-MM-dd";

function SearchBar({
  brand,
  searchInput,
  status,
  enableUpdatedDateSearch,
  startSearchUpdatedDate,
  endSearchUpdatedDate,
}: Props) {
  const [isSearchUpdatedDate, setIsSearchUpdatedDate] = useState(
    enableUpdatedDateSearch
  );
  const [startUpdatedDate, setStartUpdatedDate] = useState(
    startSearchUpdatedDate
  );
  const [endUpdatedDate, setEndUpdatedDate] = useState(endSearchUpdatedDate);
  const [searchString, setSearchString] = useState<string>(searchInput);
  const [searchBrandOption, setSearchBrandOption] = useState<string>(brand);
  const [searchStatusOption, setSearchStatusOption] = useState<string>(status);
  const navigate = useNavigate();

  const DropdownIndicator = (
    props: DropdownIndicatorProps<SearchOption, true>
  ) => {
    return (
      <components.DropdownIndicator {...props}>
        <BiSearchAlt size={20} />
      </components.DropdownIndicator>
    );
  };

  const filterProducts = async (inputValue: string) => {
    const vars = buildProductSearchVars({
      q: inputValue,
      scope: searchBrandOption,
      status: searchStatusOption,
      limit: 10,
      from: 0,
      startUpdatedDate: isSearchUpdatedDate ? startUpdatedDate?.toISOString() : "",
      endUpdatedDate: isSearchUpdatedDate ? endUpdatedDate?.toISOString() : "",
    });

    const result = await API.graphql(graphqlOperation(searchProductsQuery, vars));
    const products = extractSearchProductsV2(result);

    return [{ label: inputValue, value: "custom" }, ...products];
  };

  const promiseOptions = (inputValue: string) =>
    new Promise<SearchOption[]>((resolve) => {
      setTimeout(() => {
        resolve(filterProducts(inputValue));
      }, 100);
    });

  return (
    <div className="p-3 mb-3 rounded shadow-lg bg-slate-100">
      <div className="flex flex-row gap-2">
        <div>
          <button
            type="button"
            data-te-ripple-init
            data-te-ripple-color="light"
            data-te-collapse-init
            data-te-target="#collapseExample"
            aria-expanded="false"
            aria-controls="collapseExample"
            className="inline-block rounded-full bg-primary p-2 uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
          >
            <FcFilledFilter size={20} />
          </button>
        </div>

        <div className="flex flex-row w-full gap-2">
          <AsyncSelect
            className="w-full"
            isClearable
            isSearchable
            onChange={(e: any) => {
              if (!e) {
                setSearchString("");
                navigate(
                  !isSearchUpdatedDate
                    ? `/search?scope=${searchBrandOption}&status=${searchStatusOption}`
                    : `/search?scope=${searchBrandOption}&status=${searchStatusOption}&updated_start=${format(
                        startUpdatedDate,
                        dateFormat
                      )}&updated_end=${format(endUpdatedDate, dateFormat)}`
                );
              } else if (e.value === "custom") {
                setSearchString(e.label);
                navigate(
                  !isSearchUpdatedDate
                    ? `/search?q=${e.label}&scope=${searchBrandOption}&status=${searchStatusOption}`
                    : `/search?q=${
                        e.label
                      }&scope=${searchBrandOption}&status=${searchStatusOption}&updated_start=${format(
                        startUpdatedDate,
                        dateFormat
                      )}&updated_end=${format(endUpdatedDate, dateFormat)}`
                );
              } else {
                navigate(`/product/${e.value.id}/show`);
              }
            }}
            // defaultInputValue={searchInput}
            placeholder={searchInput ? searchInput : "Search Products"}
            loadOptions={promiseOptions}
            components={{
              Option: CustomOption,
              DropdownIndicator: DropdownIndicator,
            }}
          />
        </div>
      </div>

      <div
        className="!visible hidden mt-2"
        id="collapseExample"
        data-te-collapse-item
      >
        <div className="block rounded-lg bg-white p-6 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] dark:bg-neutral-700 dark:text-neutral-50">
          <div>
            <span className="font-bold">Brand</span>
            <Select
              className="w-full px-4 py-2"
              defaultValue={searchOptions.find((so: any) => so.value === brand)}
              options={searchOptions}
              onChange={(e: any) => {
                setSearchBrandOption(e.value);
              }}
            />
          </div>

          {/* <div className="my-2">
            <span className="font-bold">Tag(s)</span>
            <Select
              className="w-full px-4 py-2"
              defaultValue={productSearchStatusOptions.find(
                (st: any) => st.value === status
              )}
              options={[]}
              onChange={()=>{}}
              isMulti={true}
            />
          </div>

          <div className="my-2">
            <span className="font-bold">Function(s)</span>
            <Select
              className="w-full px-4 py-2"
              defaultValue={productSearchStatusOptions.find(
                (st: any) => st.value === status
              )}
              options={[]}
              onChange={()=>{}}
              isMulti={true}
            />
          </div> */}

          <div className="my-2">
            <span className="font-bold">Status</span>
            <Select
              className="w-full px-4 py-2"
              defaultValue={productSearchStatusOptions.find(
                (st: any) => st.value === status
              )}
              options={productSearchStatusOptions}
              onChange={(e: any) => {
                setSearchStatusOption(e.value);
              }}
            />
          </div>

          <div className="my-2">
            <div className="flex flex-row ml-6">
              <input
                className="relative float-left -ml-[1.5rem] mr-[6px] mt-[0.15rem] h-[1.125rem] w-[1.125rem] appearance-none rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300 outline-none before:pointer-events-none before:absolute before:h-[0.875rem] before:w-[0.875rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_13px_transparent] before:content-[''] checked:border-primary checked:bg-primary checked:before:opacity-[0.16] checked:after:absolute checked:after:-mt-px checked:after:ml-[0.25rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.375rem] checked:after:rotate-45 checked:after:border-[0.125rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:shadow-none focus:transition-[border-color_0.2s] focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:z-[1] focus:after:block focus:after:h-[0.875rem] focus:after:w-[0.875rem] focus:after:rounded-[0.125rem] focus:after:content-[''] checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:after:-mt-px checked:focus:after:ml-[0.25rem] checked:focus:after:h-[0.8125rem] checked:focus:after:w-[0.375rem] checked:focus:after:rotate-45 checked:focus:after:rounded-none checked:focus:after:border-[0.125rem] checked:focus:after:border-l-0 checked:focus:after:border-t-0 checked:focus:after:border-solid checked:focus:after:border-white checked:focus:after:bg-transparent dark:border-neutral-600 dark:checked:border-primary dark:checked:bg-primary dark:focus:before:shadow-[0px_0px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca]"
                type="checkbox"
                value=""
                onChange={() => {
                  setIsSearchUpdatedDate(!isSearchUpdatedDate);
                }}
                id="checkboxDefault"
                checked={isSearchUpdatedDate}
              />
              <span className="font-bold">Updated Date</span>
            </div>

            <div className="grid w-full grid-cols-1 gap-2 px-4 py-2 lg:grid-cols-2">
              <div>
                <span className="text-sm italic font-bold text-gray-500">
                  Start
                </span>
                <DatePicker
                  selected={startUpdatedDate}
                  onChange={(date: Date) => setStartUpdatedDate(date)}
                  selectsStart
                  startDate={startUpdatedDate}
                  disabled={!isSearchUpdatedDate}
                  className="form-input-control-panel-2 disabled:bg-slate-100"
                  endDate={endUpdatedDate}
                  showPopperArrow={false}
                  dateFormat="dd-MM-yyyy"
                  maxDate={new Date()}
                />
              </div>
              <div>
                <span className="text-sm italic font-bold text-gray-500">
                  End
                </span>

                <DatePicker
                  selected={endUpdatedDate}
                  onChange={(date: Date) => setEndUpdatedDate(date)}
                  selectsEnd
                  startDate={startUpdatedDate}
                  disabled={!isSearchUpdatedDate}
                  endDate={endUpdatedDate}
                  minDate={startUpdatedDate}
                  className="form-input-control-panel-2 disabled:bg-slate-100"
                  showPopperArrow={false}
                  dateFormat="dd-MM-yyyy"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-row justify-center">
            <a
              type="button"
              href={
                !isSearchUpdatedDate
                  ? `/search?q=${searchString}&scope=${searchBrandOption}&status=${searchStatusOption}`
                  : `/search?q=${searchString}&scope=${searchBrandOption}&status=${searchStatusOption}&updated_start=${format(
                      startUpdatedDate,
                      dateFormat
                    )}&updated_end=${format(endUpdatedDate, dateFormat)}`
              }
              className="inline-block rounded-full bg-primary px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
            >
              <span className="flex flex-row gap-2">
                <BiSearchAlt size={18} /> <span> Search</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
export default SearchBar;
